import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../api/client";
import { esciDalPass, leggiPass, togliPass } from "../lib/passPingup";

// Create the tenant context
// Esportato per chi deve funzionare anche fuori dal provider (AvvisoLimitePiano).
export const TenantContext = createContext(null);

// Tenant provider component
export const TenantProvider = ({ children }) => {
  const { user, tenants, isAuthenticated, isLoading: authLoading } = useAuth();
  const [activeTenant, setActiveTenantState] = useState(null);
  const [billingAccount, setBillingAccount] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingBilling, setIsFetchingBilling] = useState(false);
  // Il pass di PingUp (lib/passPingup.js): vale solo per gli amministratori di sistema.
  const [passSalvato] = useState(leggiPass);
  const pass = user?.type === "admin" ? passSalvato : null;

  // Initialize active tenant from localStorage or first tenant
  useEffect(() => {
    console.log("[TenantContext] useEffect triggered", {
      authLoading,
      isAuthenticated,
      tenants,
      tenantsLength: tenants?.length,
    });

    if (authLoading || !isAuthenticated) {
      setIsLoading(false);
      return;
    }

    // Dentro come PingUp: l'organizzazione del cliente, che non e' fra le sue.
    if (pass) {
      setActiveTenantState(pass.tenant);
      localStorage.setItem("activeTenantId", pass.tenant.tenant_id);
      setIsLoading(false);
      return;
    }
    // Un pass rimasto da un amministratore, con un altro utente: non vale.
    if (passSalvato && user && user.type !== "admin") togliPass();

    const savedTenantId = localStorage.getItem("activeTenantId");
    console.log("[TenantContext] savedTenantId:", savedTenantId);

    if (tenants && tenants.length > 0) {
      let tenant = null;

      // Try to restore saved tenant (only if it's a valid ID)
      if (
        savedTenantId &&
        savedTenantId !== "undefined" &&
        savedTenantId !== "null"
      ) {
        tenant = tenants.find(
          (t) => t.tenant_id === savedTenantId || t._id === savedTenantId
        );
        console.log("[TenantContext] Found saved tenant:", tenant);
      }

      // Fallback to first tenant
      if (!tenant) {
        tenant = tenants[0];
        console.log("[TenantContext] Using first tenant:", tenant);
      }

      const tenantId = tenant.tenant_id || tenant._id;
      console.log("[TenantContext] Final tenantId:", tenantId);

      if (tenantId && tenantId !== "undefined") {
        setActiveTenantState(tenant);
        localStorage.setItem("activeTenantId", tenantId);
        console.log("[TenantContext] Set active tenant:", tenant);
      } else {
        setActiveTenantState(null);
        localStorage.removeItem("activeTenantId");
        console.log("[TenantContext] Invalid tenantId, cleared");
      }
    } else {
      setActiveTenantState(null);
      localStorage.removeItem("activeTenantId");
      console.log("[TenantContext] No tenants available");
    }

    setIsLoading(false);
  }, [tenants, isAuthenticated, authLoading, pass, passSalvato, user]);

  // Fetch billing account and usage data when tenant changes
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!activeTenant || !isAuthenticated) {
        setBillingAccount(null);
        setUsageData(null);
        return;
      }

      setIsFetchingBilling(true);
      try {
        // Fetch billing account info (only works for master org)
        if (activeTenant.is_master) {
          try {
            const accountData = await api.get("/billing/account");
            setBillingAccount(accountData);
          } catch (error) {
            if (error.response?.status !== 403) {
              console.error("Failed to fetch billing account:", error);
            }
            setBillingAccount(null);
          }
        } else {
          setBillingAccount(null);
        }

        // Fetch usage data (works for both master and sub-org)
        try {
          const usage = await api.get("/billing/usage");
          setUsageData(usage);
        } catch (error) {
          console.error("Failed to fetch usage data:", error);
          setUsageData(null);
        }
      } finally {
        setIsFetchingBilling(false);
      }
    };

    fetchBillingData();
  }, [activeTenant, isAuthenticated]);

  // Switch to a different tenant
  const switchTenant = (tenant) => {
    if (!tenant) return;

    const tenantId = tenant.tenant_id || tenant._id;

    // Validate tenantId before saving
    if (!tenantId || tenantId === "undefined" || tenantId === "null") {
      console.error("Invalid tenant ID:", tenantId);
      return;
    }

    // Passare a una delle proprie organizzazioni chiude il pass di PingUp.
    togliPass();
    setActiveTenantState(tenant);
    localStorage.setItem("activeTenantId", tenantId);

    // Reload the page to refresh all data with new tenant context
    window.location.reload();
  };

  // Create a new tenant
  const createTenant = async (tenantName) => {
    try {
      const result = await api.post("/tenants", {
        name: tenantName,
      });

      // Refresh auth to get updated tenant list
      await api.auth.refresh();

      // Switch to the new tenant
      if (result.tenant) {
        switchTenant(result.tenant);
      }

      return result;
    } catch (error) {
      console.error("Failed to create tenant:", error);
      throw error;
    }
  };

  // Helper functions
  const isMasterOrg = activeTenant?.is_master === true;
  // L'abbonamento lo gestisce solo il titolare (sull'API can_manage_billing e'
  // suo e degli amministratori di sistema PingUp): non gli amministratori
  // dell'organizzazione ne' i membri. Senza ruolo noto decide l'API.
  const titolare = !activeTenant?.role || activeTenant.role === "owner" || user?.type === "admin";
  const hasParent = activeTenant?.parent_tenant_id != null;

  // Get master org from tenants list
  const masterOrg = tenants?.find(
    (t) => t.is_master && t.owner_user_id === activeTenant?.owner_user_id
  );

  // Get sub-orgs for current master
  const subOrgs = isMasterOrg
    ? tenants?.filter(
        (t) =>
          t.parent_tenant_id === activeTenant?._id ||
          t.parent_tenant_id === activeTenant?.tenant_id
      )
    : [];

  const contextValue = {
    activeTenant,
    tenants: tenants || [],
    isLoading,
    isFetchingBilling,
    switchTenant,
    createTenant,
    // Billing data
    billingAccount,
    usageData,
    // Master/Sub-org helpers
    isMasterOrg,
    hasParent,
    masterOrg,
    subOrgs,
    canManageBilling: isMasterOrg && titolare,
    canManagePlan: isMasterOrg && titolare,
    canCreateSubOrg: isMasterOrg,
    // Dentro un cliente come PingUp: il pass (cliente, da quando) e come uscire.
    pass,
    esciDalPass,
    adminDiSistema: user?.type === "admin",
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
};

// Hook to use tenant context
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
